import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import AuthLayout from '../components/layout/AuthLayout';
import Input, { LockIcon } from '../components/ui/Input';
import Button from '../components/ui/Button';
import { authService } from '../services/authService';
import './Auth.css';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('form'); // form, success, error
  const [message, setMessage] = useState('');
  const [token, setToken] = useState('');

  const closeError = () => {
    setErrors({});
  };

  useEffect(() => {
    const urlToken = searchParams.get('token');
    if (!urlToken) {
      setStatus('error');
      setMessage('Invalid reset link. No token provided.');
    } else {
      setToken(urlToken);
    }
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters';
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (!token) {
      setErrors({ general: 'Invalid reset token. Please request a new password reset.' });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const response = await authService.resetPassword(token, formData.newPassword);
      console.log('Password reset successful:', response);
      
      setStatus('success');
      setMessage('Your password has been reset successfully! You can now login with your new password.');
      
      // Auto redirect after 5 seconds
      setTimeout(() => {
        navigate('/login');
      }, 5000);
      
    } catch (error) {
      console.error('Password reset failed:', error);
      
      // Handle different types of errors
      let errorMessage = 'Password reset failed. Please try again.';
      
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const data = error.response.data;
        
        if (status === 400) {
          errorMessage = data?.message || 'Invalid or expired reset token. Please request a new password reset.';
        } else if (status === 404) {
          errorMessage = 'Reset token not found. Please request a new password reset.';
        } else if (status === 429) {
          errorMessage = 'Too many reset attempts. Please try again later.';
        } else if (status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else if (data && data.message) {
          errorMessage = data.message;
        }
      } else if (error.message) {
        // Network or other errors
        if (error.message.includes('Failed to connect')) {
          errorMessage = 'Cannot connect to server. Please check your internet connection.';
        } else if (error.message.includes('Network Error')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setErrors({
        general: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return (
          <div className="verification-icon success">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" fill="#10B981"/>
              <path d="m9 12 2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        );
      case 'error':
        return (
          <div className="verification-icon error">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" fill="#EF4444"/>
              <path d="m15 9-6 6m0-6 6 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  if (status === 'success') {
    return (
      <AuthLayout>
        <div className="auth-container">
          <div className="auth-card">
            <div className="verification-content">
              {getStatusIcon()}
              
              <h2 className="auth-title">Password Reset Successful!</h2>
              
              <p className="verification-message">{message}</p>
              
              <div className="verification-success">
                <p className="redirect-message">
                  Redirecting to login in 5 seconds...
                </p>
                <button
                  onClick={() => navigate('/login')}
                  className="btn btn-primary"
                >
                  Go to Login Now
                </button>
              </div>
            </div>
          </div>
        </div>
      </AuthLayout>
    );
  }

  if (status === 'error') {
    return (
      <AuthLayout>
        <div className="auth-container">
          <div className="auth-card">
            <div className="verification-content">
              {getStatusIcon()}
              
              <h2 className="auth-title">Reset Link Invalid</h2>
              
              <p className="verification-message">{message}</p>
              
              <div className="verification-actions">
                <button
                  onClick={() => navigate('/forgot-password')}
                  className="btn btn-primary"
                >
                  Request New Reset Link
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className="btn btn-link"
                >
                  Back to Login
                </button>
              </div>
            </div>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="auth-form">
        <div className="auth-header">
          <h1 className="auth-title">Reset Your Password</h1>
          <p className="auth-subtitle">Enter your new password below</p>
        </div>

        <div className="auth-content">
          <form onSubmit={handleSubmit} className="auth-form-fields">
            {errors.general && (
              <div className="error-message general-error">
                <span className="message-text">{errors.general}</span>
                <button 
                  type="button"
                  className="message-close-btn"
                  onClick={closeError}
                  aria-label="Close error message"
                >
                  ×
                </button>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">New Password</label>
              <Input
                type="password"
                name="newPassword"
                placeholder="Enter new password"
                value={formData.newPassword}
                onChange={handleChange}
                error={errors.newPassword}
                icon={LockIcon}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Confirm New Password</label>
              <Input
                type="password"
                name="confirmPassword"
                placeholder="Confirm new password"
                value={formData.confirmPassword}
                onChange={handleChange}
                error={errors.confirmPassword}
                icon={LockIcon}
              />
            </div>

            <Button
              type="submit"
              className="submit-button"
              disabled={loading}
            >
              {loading ? 'Resetting Password...' : 'Reset Password'}
            </Button>

            <div className="auth-footer">
              <div className="auth-links">
                <span>Remember your password? </span>
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="btn btn-link"
                  style={{ padding: 0, fontSize: 'inherit' }}
                >
                  Sign in
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </AuthLayout>
  );
};

export default ResetPassword;
